import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type EventsFilter, api } from '../lib/api.js';

export const useStats = () =>
  useQuery({ queryKey: ['stats'], queryFn: api.stats, refetchInterval: 60_000 });

export const useZones = () =>
  useQuery({ queryKey: ['zones'], queryFn: api.zones, refetchInterval: 60_000 });

export const useZone = (id: number | null) =>
  useQuery({
    queryKey: ['zone', id],
    queryFn: () => api.zone(id!),
    enabled: id != null,
  });

export const useEvents = (filter: EventsFilter) =>
  useQuery({
    queryKey: ['events', filter],
    queryFn: () => api.events(filter),
    refetchInterval: 60_000,
  });

export const useEvent = (id: number | null) =>
  useQuery({ queryKey: ['event', id], queryFn: () => api.event(id!), enabled: id != null });

export const useRuns = () =>
  useQuery({ queryKey: ['runs'], queryFn: api.runs, refetchInterval: 15_000 });

export const useCursors = () => useQuery({ queryKey: ['cursors'], queryFn: api.cursors });

export const useBrief = () =>
  useQuery({ queryKey: ['brief'], queryFn: api.brief, refetchInterval: 5 * 60_000 });

export const useAiUsage = () =>
  useQuery({ queryKey: ['ai-usage'], queryFn: api.aiUsage, refetchInterval: 30_000 });

export const useSeedPresets = () =>
  useQuery({
    queryKey: ['seed-presets'],
    queryFn: api.seedPresets,
    staleTime: Number.POSITIVE_INFINITY,
  });

export function useSeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (preset?: string) => api.seed(preset),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useImportCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.importCsv(file),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useAcknowledgeZone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (zoneId: number) => api.acknowledgeZone(zoneId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zone'] }),
  });
}
